import React from 'react';
import { browserHistory } from 'react-router';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import { can } from '../../access';
import { loanBidsApi } from '../../requests/loanBids';
import { writeMailMergeSelection } from '../../utils';
import LoanBidRow from './LoanBidRow';
import LenderBulkPicker from './LenderBulkPicker';
import './LoanBids.scss';

let nextCid = 1;
const newRow = (lender = null) => ({ cid: nextCid++, lender });

/**
 * LOAN BIDS: the lenders a broker means to pitch this property's loan to.
 * Saves through its own endpoint, so it works in view mode and never touches
 * the property page's unsaved edits.
 */
export default function LoanBids({ propertyId, propertyName }) {
    const [open, setOpen] = React.useState(false);
    const [rows, setRows] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [dirty, setDirty] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [loadFailed, setLoadFailed] = React.useState(false);
    const [bulkOpen, setBulkOpen] = React.useState(false);

    if (!propertyId || !can('property.peak_sections', 'view')) return null;

    const editable = can('property.peak_sections', 'edit');
    const canMerge = editable && can('mail_merge');
    // While the list is loading, or failed to load, rows is empty: saving then
    // would replace the stored list with nothing.
    const blocked = loading || loadFailed;

    const openDialog = () => {
        setOpen(true);
        setLoading(true);
        setError(null);
        setLoadFailed(false);
        setDirty(false);
        setBulkOpen(false);
        setRows([]);
        loanBidsApi.list(propertyId)
            .then((lenders) => setRows(lenders.map((lender) => newRow(lender))))
            .catch((err) => { setError(err.message); setLoadFailed(true); })
            .finally(() => setLoading(false));
    };

    const change = (next) => { setRows(next); setDirty(true); };
    const pick = (cid, lender) => change(rows.map((row) => (row.cid === cid ? { ...row, lender } : row)));
    const remove = (cid) => change(rows.filter((row) => row.cid !== cid));
    // One row per picked contact, appended after the existing ones.
    const addMany = (lenders) => { change([...rows, ...lenders.map((lender) => newRow(lender))]); setBulkOpen(false); };

    const picked = rows.filter((row) => row.lender);
    const vendorIds = picked.map((row) => row.lender.vendor_id);

    // Resolves with the saved rows, so Mail Merge can save then leave.
    const save = () => {
        setSaving(true);
        setError(null);
        return loanBidsApi.replace(propertyId, vendorIds)
            .then((lenders) => {
                setRows(lenders.map((lender) => newRow(lender)));
                setDirty(false);
                return lenders;
            })
            .catch((err) => { setError(err.message); throw err; })
            .finally(() => setSaving(false));
    };

    const saveAndClose = () => save().then(() => setOpen(false)).catch(() => {});

    const mailMerge = () => (dirty ? save() : Promise.resolve())
        .then(() => {
            writeMailMergeSelection({
                audience: 'lenders',
                propertyIds: [String(propertyId)],
                propertyName: propertyName || '',
                capturedAt: new Date().toISOString(),
            });
            browserHistory.push('/mail-merge');
        })
        .catch(() => {});

    return (
        <React.Fragment>
            <button type="button" className="button-new positive LoanBids-button" onClick={openDialog}>
                Loan Bids
            </button>
            {/* Above the sidebar (z-index 9999), like the app's other dialogs. */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth style={{ zIndex: 10002 }} aria-labelledby="loan-bids-title">
                <DialogTitle id="loan-bids-title">Loan Bids{propertyName ? ' — ' + propertyName : ''}</DialogTitle>
                <DialogContent>
                    {error ? <div className="LoanBids-error" role="alert">{error}</div> : null}
                    <div className="LoanBids-row LoanBids-row--head">
                        <div>Lender Contact</div><div>Lender</div><div>Work</div><div>Cell</div><div>Email</div><div/>
                    </div>
                    {loading ? <div className="LoanBids-muted">Loading…</div> : rows.map((row) => (
                        <LoanBidRow
                            key={row.cid}
                            row={row}
                            editable={editable}
                            takenIds={vendorIds.filter((id) => !row.lender || id !== row.lender.vendor_id)}
                            onPick={pick}
                            onRemove={remove}
                        />
                    ))}
                    {!loading && rows.length === 0 ? <div className="LoanBids-muted">No lenders yet.</div> : null}
                    {editable && bulkOpen ? (
                        <LenderBulkPicker takenIds={vendorIds} onAdd={addMany} onCancel={() => setBulkOpen(false)} />
                    ) : null}
                    {editable && !bulkOpen ? <button type="button" className="button-new positive LoanBids-add" onClick={() => setBulkOpen(true)} disabled={blocked || saving}>Add</button> : null}
                </DialogContent>
                <DialogActions>
                    {canMerge ? (
                        <button type="button" className="button-new positive" disabled={blocked || saving || picked.length === 0} onClick={mailMerge}>
                            Mail Merge
                        </button>
                    ) : null}
                    <button type="button" className="button-new" onClick={() => setOpen(false)} disabled={saving}>
                        {editable ? 'Cancel' : 'Close'}
                    </button>
                    {editable ? (
                        <button type="button" className="button-new positive" onClick={saveAndClose} disabled={blocked || saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    ) : null}
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}
